import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import type { BillingCycle, SubscriptionPlan } from '@techsofit/api-types';
import { useAuth } from '@techsofit/app-kit';
import { Navigate } from 'react-router-dom';
import { Button, Card, Logo, Skeleton, cn } from '@techsofit/ui';
import { getPublicPricing } from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';
import { money } from '../lib/format';

/** Public tier picker. "Sign up" without a chosen tier lands here; picking one → /signup?plan=. */
export function PricingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const as = params.get('as'); // 'trainer' | 'gym' — carried from the onboarding chooser
  const { status } = useAuth();
  const [annual, setAnnual] = useState(true);
  const { data, loading } = useAsync(getPublicPricing, []);

  if (status === 'authenticated') return <Navigate to="/dashboard" replace />;

  const plans = data?.plans ?? [];
  const discount = data?.annualDiscountPercent ?? 20;

  // Columns and width track the number of plans, so 2 plans sit centered and
  // balanced rather than leaving an empty third column.
  const count = plans.length;
  const gridCls =
    count <= 1
      ? 'max-w-sm grid-cols-1'
      : count === 2
        ? 'max-w-3xl grid-cols-1 sm:grid-cols-2'
        : 'max-w-5xl grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  function choose(plan: SubscriptionPlan) {
    const cycle: BillingCycle = annual ? 'ANNUAL' : 'MONTHLY';
    navigate(`/signup?plan=${plan.id}&cycle=${cycle.toLowerCase()}${as ? `&as=${as}` : ''}`);
  }

  return (
    <div className="min-h-dvh bg-canvas">
      <div className="mx-auto max-w-5xl px-4 py-10 md:py-16">
        <div className="flex flex-col items-center text-center">
          <Logo />
          <h1 className="mt-6 font-display text-display-md text-ink">Pick a plan to get started</h1>
          <p className="mt-2 max-w-md text-sm text-ink-soft">
            Start free and upgrade any time. You can change plans whenever you like.
          </p>

          <div className="mt-6 inline-flex items-center gap-1 rounded-pill bg-card p-1 shadow-card">
            <button onClick={() => setAnnual(false)} aria-pressed={!annual} className={cn('rounded-pill px-4 py-1.5 text-sm font-medium transition-colors', !annual ? 'bg-dark text-on-dark' : 'text-ink-soft')}>
              Monthly
            </button>
            <button onClick={() => setAnnual(true)} aria-pressed={annual} className={cn('inline-flex items-center gap-1.5 rounded-pill px-4 py-1.5 text-sm font-medium transition-colors', annual ? 'bg-dark text-on-dark' : 'text-ink-soft')}>
              Annual <span className="rounded-pill bg-accent px-1.5 py-0.5 text-[0.625rem] text-accent-ink">−{discount}%</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="mx-auto mt-10 grid max-w-3xl grid-cols-1 gap-5 sm:grid-cols-2">
            {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-80" />)}
          </div>
        ) : (
          <div className={cn('mx-auto mt-10 grid items-start gap-5', gridCls)}>
            {plans.map((p) => {
              const monthly = Number(p.monthlyPrice);
              const free = monthly === 0;
              // Effective monthly price (same framing as the marketing site).
              const price = annual ? Math.round((monthly * (100 - discount)) / 100) : monthly;
              return (
                <Card key={p.id} tone={p.popular ? 'dark' : 'card'} className={cn('flex flex-col p-6', p.popular && 'lg:-mt-3')}>
                  <div className="flex items-center justify-between">
                    <p className={cn('font-display text-lg font-semibold', p.popular ? 'text-on-dark' : 'text-ink')}>{p.name}</p>
                    {p.popular && <span className="rounded-pill bg-accent px-2.5 py-1 text-xs font-medium text-accent-ink">Popular</span>}
                  </div>
                  {p.tagline && <p className={cn('text-sm', p.popular ? 'text-on-dark-muted' : 'text-muted')}>{p.tagline}</p>}

                  <div className="mt-4 flex items-baseline gap-1">
                    <span className={cn('font-display text-display-md tabular', p.popular ? 'text-on-dark' : 'text-ink')}>{money(price)}</span>
                    <span className={cn('text-sm', p.popular ? 'text-on-dark-muted' : 'text-muted')}>{free ? '' : annual ? '/mo, billed yearly' : '/mo'}</span>
                  </div>
                  <p className={cn('mt-1 text-sm', p.popular ? 'text-on-dark-muted' : 'text-muted')}>
                    {p.memberCap == null ? 'Unlimited members' : `Up to ${p.memberCap} members`}
                    {' · '}
                    {p.staffCap == null ? 'unlimited staff' : `up to ${p.staffCap} staff`}
                  </p>

                  <Button block variant={p.popular ? 'primary' : free ? 'dark' : 'outline'} className="mt-5" onClick={() => choose(p)}>
                    {free ? 'Start free' : `Choose ${p.name}`}
                  </Button>

                  <ul className="mt-6 space-y-2.5">
                    {p.features.map((f) => (
                      <li key={f} className={cn('flex items-start gap-2 text-sm', p.popular ? 'text-on-dark-muted' : 'text-ink-soft')}>
                        <Check className={cn('mt-0.5 size-4 shrink-0', p.popular ? 'text-accent' : 'text-ok')} aria-hidden /> {f}
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>
        )}

        <p className="mt-8 text-center text-sm text-muted">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-ink underline-offset-2 hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
